import * as React from 'react';
import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import urlJoin from 'url-join';

import config from 'js/app/config';
import { getCmsLocaleString } from 'js/lib/language';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { useLocation, useParams } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import {
  DdpFieldsAboveFoldFragmentDoc,
  DdpFieldsBelowFoldFragmentDoc,
} from 'bundles/degree-description/__generated__/contentfulOperations';
import type {
  DdpFieldsAboveFoldFragment,
  DdpFieldsBelowFoldFragment,
} from 'bundles/degree-description/__generated__/contentfulTypes';
import DegreeDescriptionPageContext from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { NotFound } from 'bundles/degree-description/pages/consumer/ConsumerPageGuard';
import type {
  GetAppFeeWaiverQualificationBySlugQuery,
  GetAppFeeWaiverQualificationBySlugQueryVariables,
} from 'bundles/degree-description/queries/__generated__/getAppFeeWaiverQualificationBySlug';
import type {
  GetAverageCourseStarRatingBySlugQuery,
  GetAverageCourseStarRatingBySlugQueryVariables,
} from 'bundles/degree-description/queries/__generated__/getAverageCourseStarRatingBySlug';
import type { GetDegreeBySlugQuery } from 'bundles/degree-description/queries/__generated__/getDegreeBySlug';
import type { GetDdpVariantContentQuery } from 'bundles/degree-description/queries/__generated__/getVariantContent';
import getAppFeeWaiverQualificationBySlug from 'bundles/degree-description/queries/getAppFeeWaiverQualificationBySlug.graphql';
import getAverageCourseStarRatingBySlug from 'bundles/degree-description/queries/getAverageCourseStarRatingBySlug.graphql';
import getVariantContent from 'bundles/degree-description/queries/getVariantContent.graphql';
import type { AverageCourseStarRating } from 'bundles/degree-description/types';
import { GET_COMPLETED_QUALIFICATION_SURVEY } from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import type {
  RetrieveCompletedSurveyResponse,
  RetrieveCompletedSurveyVariables,
} from 'bundles/expression-of-interest/queries/RetrieveCompletedQualificationSurvey';
import { EOI_SUBMISSION_LOOKBACK_PERIOD } from 'bundles/expression-of-interest/utils/util';
import { getLocalizedQueryVariables } from 'bundles/internationalization-lib/components/WithLocalizedContent';
import type { AvailabilityStatus } from 'bundles/premium-hub/types';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';
import getDegreeBySlug from 'bundles/xddp/components/__queries__/getDegreeBySlug.graphql';

type PropsFromAverageCourseStarRatingQuery = {
  averageCourseStarRating?: AverageCourseStarRating;
};

type PropsFromHasAppFeeWaiverQuery = {
  hasAppFeeWaiverQualification?: boolean;
};

type PropsFromGetDegreeBySlugQueryVariables = { locale: string; slug: string };

type PropsToDataFetcher = PropsFromAverageCourseStarRatingQuery &
  PropsFromHasAppFeeWaiverQuery & {
    pageContentAboveFold?: DdpFieldsAboveFoldFragment | null;
    pageContentBelowFold?: DdpFieldsBelowFoldFragment | null;
    loading: boolean;
  };

type PropsToInnerProvider = PropsToDataFetcher & {
  children: React.ReactNode;
  id: string;
  productVariant?: string;
  degreeAvailabilityStatus: AvailabilityStatus;
  blockedCountries: string[];
  eoiSurveyData?: RetrieveCompletedSurveyResponse;
  loadingEoiSurveyData: boolean;
};

const DegreeDescriptionDataPageDataProviderInnerComponent = ({
  id,
  productVariant,
  loading,
  pageContentAboveFold,
  pageContentBelowFold,
  blockedCountries,
  degreeAvailabilityStatus,
  hasAppFeeWaiverQualification,
  averageCourseStarRating,
  eoiSurveyData,
  loadingEoiSurveyData,
  children,
}: PropsToInnerProvider) => {
  const pageContent = useMemo(
    () => ({
      ...pageContentAboveFold,
      ...pageContentBelowFold,
    }),
    [pageContentAboveFold, pageContentBelowFold]
  );

  return (
    <DegreeDescriptionPageContext.Provider
      value={useMemo(
        () => ({
          id,
          productVariant,
          loading,
          pageContent,
          degreeAvailabilityStatus,
          blockedCountries,
          hasAppFeeWaiverQualification,
          averageCourseStarRating,
          eoiSurveyData,
          loadingEoiSurveyData,
        }),
        [
          id,
          productVariant,
          loading,
          pageContent,
          degreeAvailabilityStatus,
          blockedCountries,
          hasAppFeeWaiverQualification,
          averageCourseStarRating,
          eoiSurveyData,
          loadingEoiSurveyData,
        ]
      )}
    >
      {children}
    </DegreeDescriptionPageContext.Provider>
  );
};

const DegreeDescriptionDataPageDataProviderInner = React.memo(DegreeDescriptionDataPageDataProviderInnerComponent);

export const DegreeDescriptionPageDataProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    search,
    query: { preview },
  } = useLocation();
  const { slug } = useParams();

  const pageUrl = urlJoin(config.url.base, `degrees/${slug}`, search ?? '');

  const isPreview = Number(preview) === 1;

  const { data: degreeBySlugData } = useQuery<GetDegreeBySlugQuery, PropsFromGetDegreeBySlugQueryVariables>(
    getDegreeBySlug,
    {
      variables: { locale: getCmsLocaleString(), slug },
      context: {
        clientName: 'gatewayGql',
        errorPolicy: 'all',
      },
    }
  );

  const { data: appFeeWaiverData } = useQuery<
    GetAppFeeWaiverQualificationBySlugQuery,
    GetAppFeeWaiverQualificationBySlugQueryVariables
  >(getAppFeeWaiverQualificationBySlug, {
    skip: !isAuthenticatedUser(),
    variables: { degreeSlug: slug },
    context: { clientName: 'gatewayGql' },
  });

  const { data: averageCourseStarRatingData } = useQuery<
    GetAverageCourseStarRatingBySlugQuery,
    GetAverageCourseStarRatingBySlugQueryVariables
  >(getAverageCourseStarRatingBySlug, {
    skip: !slug,
    variables: { slug },
    context: { clientName: 'gatewayGql' },
  });
  // TODO: Fix this ID transforming behavior.
  // The ID coming back from PremiumProductCollections needs some extra handling and
  // doesn't match how the ID from our Scala-based resource functions.
  const id = degreeBySlugData?.PremiumProductCollections?.getBySlug?.id;
  const degreeId = id?.replace(/^degree~/, 'base~') ?? '';
  const productId = degreeId ? tupleToStringKey([ProductTypes.DEGREE, degreeId]) : '';
  const productVariant = degreeBySlugData?.PremiumProductCollections?.getBySlug?.productVariant;

  const pageProps = {
    averageCourseStarRating: averageCourseStarRatingData?.Degree?.queryBySlug?.averageCourseStarRating,
    hasAppFeeWaiverQualification: appFeeWaiverData?.AppFeeWaiver?.hasAppFeeWaiverQualification,
    degreeAvailabilityStatus: degreeBySlugData?.PremiumProductCollections?.getBySlug?.catalogAvailabilityStatus ?? '',
    blockedCountries: (degreeBySlugData?.PremiumProductCollections?.getBySlug?.blockedCountries ?? []) as string[],
    id: degreeId,
    productVariant,
  };

  const { data: surveyData, loading: loadingEoiSurveyData } = useQuery<
    RetrieveCompletedSurveyResponse,
    RetrieveCompletedSurveyVariables
  >(GET_COMPLETED_QUALIFICATION_SURVEY, {
    variables: {
      productId,
      eoiContext: { source: pageUrl, utmParameters: {} },
      lookbackPeriodInMonths: EOI_SUBMISSION_LOOKBACK_PERIOD,
    },
    skip: !productId,
  });

  const afLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'universityProgramDegreeDescriptionPage',
    fragmentDoc: DdpFieldsAboveFoldFragmentDoc,
    fragmentFieldsName: 'DdpFieldsAboveFold',
    filters: { slug },
  });

  const bfLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'universityProgramDegreeDescriptionPage',
    fragmentDoc: DdpFieldsBelowFoldFragmentDoc,
    fragmentFieldsName: 'DdpFieldsBelowFold',
    filters: { slug },
  });

  const afLocalizedVariantQueryVars = {
    ...afLocalizedQueryVars?.contentfulRequest?.variables,
    query: afLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
  };

  const bfLocalizedVariantQueryVars = {
    ...bfLocalizedQueryVars?.contentfulRequest?.variables,
    query: bfLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    slug,
  };

  /**
   * Why are there two calls using WithLocalizedContent? That's because
   * the query for this page is too big for just one call! Contentful recommends
   * splitting large queries into smaller ones in this case.
   * https://www.contentful.com/developers/docs/references/graphql/#/reference/graphql-errors/graphql-errors-explained
   */
  const {
    loading: aboveLoading,
    data: aboveFoldData,
    error: aboveError,
  } = useQuery(getVariantContent, {
    variables: afLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
  });
  const {
    loading: belowLoading,
    data: belowFoldData,
    error: belowError,
  } = useQuery(getVariantContent, {
    variables: bfLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
    ssr: false, // offload non-critical part (below fold contentful query) to client side to improve TTFB
  });

  if ((!aboveLoading && aboveError) || (!belowLoading && belowError)) {
    return <NotFound />;
  }

  const aboveFoldContentData = (aboveFoldData as GetDdpVariantContentQuery)?.LocalizedData?.getVariantContent
    ?.contentDataJson;
  const belowFoldContentData = (belowFoldData as GetDdpVariantContentQuery)?.LocalizedData?.getVariantContent
    ?.contentDataJson;

  // Returns as "any" from the autogenerated type, but is actually a JSON object.
  // Using some coercion to force TypeScript to know what this should be.
  const pageContentAboveFold = (aboveFoldContentData as Record<string, unknown>) ?? null;
  const pageContentBelowFold = (belowFoldContentData as Record<string, unknown>) ?? null;

  return (
    <DegreeDescriptionDataPageDataProviderInner
      pageContentAboveFold={pageContentAboveFold}
      pageContentBelowFold={pageContentBelowFold}
      loading={aboveLoading}
      hasAppFeeWaiverQualification={pageProps.hasAppFeeWaiverQualification}
      degreeAvailabilityStatus={pageProps.degreeAvailabilityStatus}
      blockedCountries={pageProps.blockedCountries}
      averageCourseStarRating={pageProps.averageCourseStarRating}
      eoiSurveyData={surveyData}
      loadingEoiSurveyData={loadingEoiSurveyData}
      id={pageProps.id}
      productVariant={pageProps.productVariant}
    >
      {children}
    </DegreeDescriptionDataPageDataProviderInner>
  );
};
